//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import FocusTrap from 'focus-trap-react';
import debounce from 'lodash/debounce';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Modal } from '@cmp/common';
import { BasicInfo, Products, CheckoutDetails } from '../../common';
//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import {
  withReferral,
  loadReferralAndStore,
  updateItem,
  removeItem,
} from '@helpers/referrals';
import { referralFormatter } from '@helpers/formatter';
import {
  handleGtmEvent,
  handleGtmEventDebounced,
  DEBOUNCE_CART_DELAY_VALUE_MS,
} from '@helpers/gtm';
//------------------------------------------------------------------------------
// Local Storage ---------------------------------------------------------------
import { isSkipStripe } from '@helpers/account';

//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class Review extends React.Component {
  constructor(props) {
    super(props);
    this.handleMethodDebounced = debounce(
      this.handleMethodDebounced.bind(this),
      DEBOUNCE_CART_DELAY_VALUE_MS
    );

    this.state = {
      editingProducts: [],
    };
    this.onEditProduct = this.onEditProduct.bind(this);
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  componentDidMount() {
    loadReferralAndStore();
    isSkipStripe();
    this.mountTrap();
  }

  componentDidUpdate() {
    const { __referral, location, history } = this.props;

    if (__referral && __referral.items.length <= 0) {
      history.push(location.state.from);
    }

    this.handleMethodDebounced();
  }

  mountTrap() {
    this.setState({ activeTrap: true });
  }
  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  onEditProduct(product, quantity) {
    let that = this;

    const { __referral, history } = this.props;
    const { editingProducts } = this.state;
    editingProducts.push(product);
    this.setState({ editingProducts });

    function removeEditingProduct() {
      const { editingProducts } = that.state;
      const filtered = editingProducts.filter((x) => x !== product);
      that.setState({ editingProducts: filtered });
    }

    if (quantity > 0) {
      updateItem(product, quantity).finally(() => removeEditingProduct());
    } else {
      removeItem(product).finally(() => removeEditingProduct());
    }
  }

  handleMethodDebounced() {
    this.handleGtmPayload();
  }

  handleGtmPayload() {
    const { __referral, t } = this.props;
    if (!__referral) return '';

    const sortedProducts = referralFormatter.sortedItems(__referral);
    if (sortedProducts.length > 0) {
      const brand = t('header_title');
      const metadata = { brand };

      const gtmPayload = {
        products: sortedProducts,
        context: this.context,
        metadata,
      };

      handleGtmEventDebounced('view_cart', gtmPayload);
    }
  }

  render() {
    const { editingProducts } = this.state;
    const { __referral, location, history, t } = this.props;
    const {
      featureFlags: { stripePayouts },
    } = this.context;
    const stripeEnabled = typeof stripePayouts === 'undefined' || stripePayouts;
    if (!__referral) return '';

    const componentClasses = classNames(styles.Details);

    const sortedProducts = referralFormatter.sortedItems(__referral);
    const brand = t('header_title');
    const metadata = { brand };

    return (
      <FocusTrap>
        <Modal.Base
          className={componentClasses}
          onClose={() => history.push(location.state.from)}
        >
          <Helmet>
            <title>{t('dashboard_ReviewYourRecommendation')}</title>
          </Helmet>

          <BasicInfo
            title={
              __referral && __referral.items && __referral.items.length > 1
                ? t('dashboard_ReviewRecommendations')
                : t('dashboard_ReviewRecommendation')
            }
            subtitleComponent={
              <span className={styles.ReviewSubtitle}>
                {t('dashboard_ReviewYourRecommendationSubtitle')}
              </span>
            }
            className={styles.BasicInfo}
          />
          <Products
            products={sortedProducts}
            onEditProduct={this.onEditProduct}
            editable={true}
            editingProducts={editingProducts}
            metadata={metadata}
          />
          <CheckoutDetails
            action={
              __referral && __referral.items && __referral.items.length > 1
                ? t('dashboard_SendRecommendations')
                : t('dashboard_SendRecommendation')
            }
            referral={__referral}
            onClick={() => {
              const pathname =
                isSkipStripe() && stripeEnabled
                  ? '/recommendation/stripe'
                  : '/recommendation/patient';
              history.push({
                pathname: pathname,
                state: { from: location.state.from, modal: true },
              });

              handleGtmEvent('begin_checkout', {
                products: sortedProducts,
                context: this.context,
                metadata,
              });
            }}
            disabled={!__referral.items || __referral.items.length <= 0}
          />
          <span className={styles.Disclosure}>
            {t('dashboard_RecommendationDisclosure')}
          </span>
        </Modal.Base>
      </FocusTrap>
    );
  }
}

Review.contextType = StoreConfigContext;
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withReferral(withTranslation()(Review));
